import axios from 'axios';
import dayjs from 'dayjs';
import Toast from '../../utils/Toast';
import { DOCUMENT_TOAST } from '../../../../constants/toastConstant';
import { manualUploadFromGmail } from '../../../../services/uploadApi';

const constructQuery = (data) =>
  data
    ?.map((group) => {
      const orConditions = group
        .map((condition) => {
          switch (condition.filterName) {
            case 'From':
              return `from:${condition.inputValue}`;
            case 'To':
              return `to:${condition.inputValue}`;
            case 'Subject':
              return `subject:${condition.inputValue}`;
            case 'Label':
              return `label:${condition.inputValue}`;
            case 'Is':
              return `is:${condition.inputValue.toLowerCase()}`;
            case 'Before':
              return `before:${dayjs(condition.inputValue).format(
                'YYYY/MM/DD',
              )}`;
            case 'After':
              return `after:${dayjs(condition.inputValue).format(
                'YYYY/MM/DD',
              )}`;
            default:
              return '';
          }
        })
        .filter(Boolean)
        .join(' OR ');
      return `(${orConditions})`;
    })
    .join(' AND ');

const extractEmail = (fromField) => {
  const emailRegex = /<([^>]+)>/;
  const match = fromField.match(emailRegex);
  return match ? match[1] : fromField;
};

const fetchAttachments = async (messageId, accessToken) => {
  try {
    const response = await axios.get(
      `https://www.googleapis.com/gmail/v1/users/me/messages/${messageId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    const { payload, id } = response.data;
    const { headers, parts } = payload;
    const subjectHeader = headers.find((header) => header.name === 'Subject');
    const fromHeader = headers.find((header) => header.name === 'From');
    const subject = subjectHeader ? subjectHeader.value : 'No Subject';
    const from = fromHeader
      ? extractEmail(fromHeader.value)
      : 'No From Address';
    const attachments = [];

    parts?.forEach((part) => {
      if (part?.filename && part?.body?.attachmentId) {
        attachments?.push({
          id,
          filename: part?.filename,
          mimeType: part?.mimeType,
          attachmentId: part?.body?.attachmentId,
          size: part?.body?.size,
          subject,
          from,
        });
      }
    });
    return attachments;
  } catch (error) {
    console.error('Error fetching attachments', error);
  }
};

const fetchEmailsRecursive = async (
  accessTokenForFetchAllEmail,
  query,
  pageToken = null,
) => {
  let allEmails = [];
  try {
    const response = await axios.get(
      'https://www.googleapis.com/gmail/v1/users/me/messages',
      {
        headers: { Authorization: `Bearer ${accessTokenForFetchAllEmail}` },
        params: {
          maxResults: 100,
          pageToken,
          q: query,
        },
      },
    );
    allEmails = allEmails.concat(response?.data?.messages || []);

    if (response?.data?.nextPageToken) {
      return fetchEmailsRecursive(
        accessTokenForFetchAllEmail,
        query,
        response.data.nextPageToken,
      );
    }
    return allEmails;
  } catch (error) {
    throw new Error(`Failed to fetch emails from Gmail: ${error?.message}`);
  }
};

const fetchAllEmails = async (
  accessTokenForFetchAllEmail,
  queryDataParameter,
) => {
  const query = `has:attachment ${constructQuery(queryDataParameter)}`;
  const allMails = await fetchEmailsRecursive(
    accessTokenForFetchAllEmail,
    query,
  );
  const attachmentPromises = await Promise.all(
    allMails?.map(async (email) =>
      fetchAttachments(email?.id, accessTokenForFetchAllEmail),
    ),
  );
  return attachmentPromises.flatMap((innerArray) => innerArray);
};

const getEmailFromGoogleAPI = async (accessToken) => {
  try {
    const response = await fetch(
      'https://www.googleapis.com/oauth2/v3/userinfo',
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error('Failed to fetch user information from Google API');
    }

    const data = await response.json();
    return data.email || null; // Return email if available
  } catch (error) {
    console.error('Error fetching email from Google:', error);
    return null;
  }
};

const handleFileForGmail = (files, accessToken) => {
  const allowedFileTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'application/pdf',
  ];
  const maxSizeInBytes = 10 * 1024 * 1024; // max size allowed 10mb
  const filesFormatNotValid = files?.filter(
    (file) => !allowedFileTypes.includes(file?.mimeType),
  );
  filesFormatNotValid?.forEach((file) => {
    Toast({
      message: `"${file.filename}" ${DOCUMENT_TOAST.FORMAT_INVALID}`,
      variant: 'error',
      position: { vertical: 'top', horizontal: 'center' },
      autoHideDuration: 4000,
    });
  });

  const filesFormatValid = files.filter((file) =>
    allowedFileTypes.includes(file?.mimeType),
  );

  const filesSizeTooLarge = filesFormatValid?.filter(
    (file) => file.size > maxSizeInBytes,
  );
  filesSizeTooLarge?.forEach((file) => {
    Toast({
      message: `"${file.name}" ${DOCUMENT_TOAST.FILE_SIZE_TOO_LARGE}`,
      variant: 'error',
      position: { vertical: 'top', horizontal: 'center' },
      autoHideDuration: 4000,
    });
  });

  const sizeLimitFilesValid = filesFormatValid?.filter(
    (file) => file.size <= maxSizeInBytes,
  );

  const fileNamesWithSpacesOrSpecialChars = sizeLimitFilesValid.filter((file) =>
    /[ !@#$%^&*]/.test(file.filename),
  );
  fileNamesWithSpacesOrSpecialChars?.forEach((file) => {
    Toast({
      message: `"${file.filename}" ${DOCUMENT_TOAST.SPACES_OR_SPECIAL_CHARS_NOT_ALLOWED}`,
      variant: 'error',
      position: { vertical: 'top', horizontal: 'center' },
      autoHideDuration: 4000,
    });
  });
  const fileNamesWithoutSpacesOrSpecialChars = sizeLimitFilesValid.filter(
    (file) => /^[^\s!@#$%^&*]+$/.test(file.filename),
  );

  const filesWithDownloadUrls = fileNamesWithoutSpacesOrSpecialChars.map(
    (file) => ({
      ...file,
      name: file.filename,
      downloadUrl: `https://www.googleapis.com/gmail/v1/users/me/messages/${file.id}/attachments/${file.attachmentId}`,
    }),
  );
  return {
    files: filesWithDownloadUrls,
    accessToken,
  };
};

export const uploadFetchedAttachments = async (
  token,
  selectedAttachments,
  documentType,
  uploadOption,
) => {
  const validateAttachments = handleFileForGmail(selectedAttachments, token);
  if (validateAttachments && validateAttachments?.files?.length > 0) {
    const { accessToken, files } = validateAttachments;
    const email = await getEmailFromGoogleAPI(token);
    return manualUploadFromGmail({
      accessToken,
      documentType,
      documentImportOption: uploadOption,
      files,
      documentImportCredential: email,
    });
  }
};

export default fetchAllEmails;
